"use client";

import React, { createContext, useContext, useState, ReactNode } from "react";
import { v4 as uuidv4 } from "uuid";

type ToastType = "success" | "error" | "info" | "loading";

interface Toast {
	id: string;
	message: string;
	type: ToastType;
}

interface ToastContextType {
	toasts: Toast[];
	addToast: (message: string, type: ToastType, duration?: number) => void;
	removeToast: (id: string) => void;
  removeLoading: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [toasts, setToasts] = useState<Toast[]>([]);

	const addToast = (message: string, type: ToastType, duration = 3000) => {
		const id = uuidv4();
		const newToast = { id, message, type };
		setToasts((prev) => [...prev, newToast]);

		if (type !== "loading") {
			setTimeout(() => {
				removeToast(id);
			}, duration);
		}
	};

	const removeLoading = () => {
		setToasts((prev) => prev.filter((toast) => toast.type !== "loading"));
	};

	const removeToast = (id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
	};

	return (
		<ToastContext.Provider value={{ toasts, addToast, removeToast, removeLoading }}>
			{children}
		</ToastContext.Provider>
	);
};

export const useToast = () => {
	const context = useContext(ToastContext);
	if (!context) throw new Error("useToast must be used within a ToastProvider");
	return context;
};
